import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { MapPin, Building2, Mail, Phone } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import { supabase } from "../utils/supabaseClient";
import "react-toastify/dist/ReactToastify.css";
import "../styles/OfficesManagement.css";

const OfficesManagement = () => {
  const [offices, setOffices] = useState({ regional: [], branch: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentOffice, setCurrentOffice] = useState(null);
  const [formData, setFormData] = useState({
    type: "regional",
    city: "",
    address: "",
    contactNumber: "",
    email: "",
    mapsLink: "",
  });

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("offices")
        .select("*")
        .order("city");

      if (error) throw error;

      // Separate offices by type
      const regionalOffices = data.filter(
        (office) => office.type === "regional"
      );
      const branchOffices = data.filter((office) => office.type === "branch");

      setOffices({
        regional: regionalOffices,
        branch: branchOffices,
      });
    } catch (error) {
      toast.error("Error fetching offices: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const officeData = {
        type: formData.type,
        city: formData.city,
        address: formData.address,
        contact_number: formData.contactNumber,
        email: formData.email,
        maps_link: formData.mapsLink,
      };

      if (isEditing) {
        const { error } = await supabase
          .from("offices")
          .update(officeData)
          .eq("id", currentOffice.id);

        if (error) throw error;
        toast.success("Office updated successfully!");
      } else {
        const { error } = await supabase.from("offices").insert([officeData]);

        if (error) throw error;
        toast.success("Office added successfully!");
      }

      resetForm();
      fetchOffices();
    } catch (error) {
      toast.error("Error: " + error.message);
    }
  };

  const handleEdit = (office) => {
    setCurrentOffice(office);
    setFormData({
      type: office.type,
      city: office.city || "",
      address: office.address || "",
      contactNumber: office.contact_number || "",
      email: office.email || "",
      mapsLink: office.maps_link || "",
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this office?")) {
      try {
        const { error } = await supabase.from("offices").delete().eq("id", id);

        if (error) throw error;
        toast.success("Office deleted successfully!");
        fetchOffices();
      } catch (error) {
        toast.error("Error deleting office: " + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: "regional",
      city: "",
      address: "",
      contactNumber: "",
      email: "",
      mapsLink: "",
    });
    setIsEditing(false);
    setCurrentOffice(null);
  };

  const OfficeCard = ({ office }) => (
    <div className="office-card">
      <div className="office-card-content">
        <h3 className="office-city">{office.city}</h3>

        {office.type === "regional" && (
          <>
            <p className="office-address">{office.address}</p>

            <div className="contact-info">
              <div className="contact-item">
                <Phone size={20} />
                <span>{office.contact_number}</span>
              </div>

              <div className="contact-item">
                <Mail size={20} />
                <span>{office.email}</span>
              </div>
            </div>

            {office.maps_link && (
              <a
                href={office.maps_link}
                target="_blank"
                rel="noopener noreferrer"
                className="maps-button"
              >
                <MapPin size={20} />
                Show on Maps
              </a>
            )}
          </>
        )}

        <div className="office-actions">
          <button onClick={() => handleEdit(office)} className="edit-button">
            Edit
          </button>
          <button
            onClick={() => handleDelete(office.id)}
            className="delete-button"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="page-content">
      <ToastContainer position="top-right" autoClose={3000} />
      <h2>{isEditing ? "Edit Office" : "Add New Office"}</h2>

      <form onSubmit={handleSubmit} className="office-form">
        <div className="form-group">
          <label htmlFor="type">Office Type</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            required
          >
            <option value="regional">Regional Office</option>
            <option value="branch">Branch Office</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            required
          />
        </div>

        {formData.type === "regional" && (
          <>
            <div className="form-group">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactNumber">Contact Number</label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="mapsLink">Google Maps Link</label>
              <input
                type="url"
                id="mapsLink"
                name="mapsLink"
                value={formData.mapsLink}
                onChange={handleInputChange}
                placeholder="https://maps.google.com/..."
              />
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="submit" className="submit-button">
            {isEditing ? "Update Office" : "Add Office"}
          </button>
          {isEditing && (
            <button type="button" onClick={resetForm} className="cancel-button">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="offices-sections">
        <section className="office-section">
          <div className="section-header">
            <Building2 size={24} />
            <h3>Regional Offices</h3>
          </div>
          <div className="offices-grid">
            {offices.regional.map((office) => (
              <OfficeCard key={office.id} office={office} />
            ))}
            {offices.regional.length === 0 && (
              <p className="no-offices">No regional offices added yet.</p>
            )}
          </div>
        </section>

        <section className="office-section">
          <div className="section-header">
            <MapPin size={24} />
            <h3>Branch Offices</h3>
          </div>
          <div className="offices-grid">
            {offices.branch.map((office) => (
              <OfficeCard key={office.id} office={office} />
            ))}
            {offices.branch.length === 0 && (
              <p className="no-offices">No branch offices added yet.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default OfficesManagement;
